import { observer } from 'mobx-react-lite';
import styled from '@emotion/styled';
import { IContentSearchResult } from '../../domain/types/IContentSearchResult';
import { Color } from '../../../../styles/Color';
import { RouterLink } from '../../../../components/common/link/RouterLink';
import { getUrlForPageId } from '../../../../utils/getUrlForLink';
import { useCommonData } from '../../../../common-data/useCommonData';
import { ChevronRightIcon, Css, ITheme, Responsive } from '../../../../ui';

interface IProps {
	result: IContentSearchResult;
}

export const SearchResultItem = observer(({ result }: IProps) => {
	const { pathToPageIdMap } = useCommonData();
	const title = result.snippet.title.value;
	const snippet = result.snippet.bodyString?.value;
	const href = getUrlForPageId(result.pageId, pathToPageIdMap);

	return (
		<StyedRouterLink href={href}>
			<Information>
				<Title
					dangerouslySetInnerHTML={{
						// eslint-disable-next-line @typescript-eslint/naming-convention
						__html: title,
					}}
				/>
				{snippet && (
					<Snippet
						dangerouslySetInnerHTML={{
							// eslint-disable-next-line @typescript-eslint/naming-convention
							__html: snippet,
						}}
					/>
				)}
			</Information>
			<ReadMore>
				<ChevronRightIcon />
			</ReadMore>
		</StyedRouterLink>
	);
});

const StyedRouterLink = styled(RouterLink)`
	padding: ${Css.pxToRem(16)} 0;
	border-bottom: 1px solid ${Color.Neutral92};
	display: flex;
	align-items: center;
	gap: ${Css.pxToRem(8)};
	text-decoration: none;

	&:first-child {
		@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
			padding-top: ${Css.pxToRem(20)};
		}
	}
`;

const Information = styled.div`
	flex: 1;
	display: flex;
	flex-direction: column;
`;

const Title = styled.h5<{}, ITheme>`
	font-size: ${Css.pxToRem(24)};
	margin: 0;
	color: ${(props) => props.theme.color.secondaryLight};
	font-weight: 500;

	& em {
		color: ${Color.OnBackground};
		font-weight: 500;
	}
`;

const Snippet = styled.p<{}, ITheme>`
	font-size: ${Css.pxToRem(16)};
	font-weight: 500;
	color: ${Color.Neutral6};
	margin: 0;
	margin-top: ${Css.pxToRem(8)};
	line-height: 1.5;
	width: 50%;
	overflow: hidden;
	display: -webkit-box;
	-webkit-line-clamp: 2;
	-webkit-box-orient: vertical;

	@media (${Responsive.getMediaQueryForBreakpoint('tablet')}) {
		width: 95%;
	}

	@media (${Responsive.getMediaQueryForBreakpoint('mobileXL')}) {
		width: 100%;
	}

	& em {
		font-weight: 500;
		color: ${Color.OnBackground};
	}
`;

const ReadMore = styled.span<{}, ITheme>`
	display: inline-flex;
	margin: 0;
	align-self: end;

	& svg path {
		fill: ${(props) => props.theme.color.secondaryLight};
	}
`;
